import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import './App.css';

import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import HowToUsePage from './pages/HowToUsePage';
import EventsPage from './pages/EventsPage';
import CoursePage from './pages/CoursePage';
import CoursesListPage from './pages/CoursesListPage';
import MustReadPopup from './components/MustReadPopup';
import InboxPage from './pages/InboxPage';
import HelpGuidesPage from './pages/HelpGuidesPage';
import InstallPrompt from './components/InstallPrompt';
import ParentAboutPage from './pages/ParentAboutPage';
import ParentHowToPage from './pages/ParentHowToPage';
import ImpactProjectPage from './pages/ImpactProjectPage';
import SJTAssessmentPage from './pages/SJTAssessmentPage';
import OrgLoginPage from './pages/OrgLoginPage';
import StaffTutorialPage from './pages/StaffTutorialPage';
import AdminTutorialPage from './pages/AdminTutorialPage';
import WorkbookPage from './pages/WorkbookPage';
import YouthTutorialPage from './pages/YouthTutorialPage';
import HomePage from './pages/HomePage';
import DemoRequestPage from './pages/DemoRequestPage';
import ProgramsPage from './pages/ProgramsPage';
import YouthDashboard from './pages/YouthDashboard';
import ThemedYouthDashboard from './components/ThemedYouthDashboard';
import CheckInWizard from './pages/CheckInWizard';
import TriviaQuiz from './pages/TriviaQuiz';
import Leaderboard from './pages/Leaderboard';
import WinsWallPage from './pages/WinsWallPage';
import Profile from './pages/Profile';
import TeamPage from './pages/TeamPage';
import CoachDashboard from './pages/CoachDashboard';
import SkillsAssessment from './pages/SkillsAssessment';
import AdminDashboard from './pages/AdminDashboard';
import AICareerCounselor from './pages/AICareerCounselor';
import InterestsExplorer from './pages/InterestsExplorer';
import SkillsTracker from './pages/SkillsTracker';
import VaultPage from './pages/VaultPage';
import CirclesPage from './pages/CirclesPage';
import PlatformAdminDashboard from './pages/PlatformAdminDashboard';
import ProgramDetailPage from './pages/ProgramDetailPage';
import ProgramAdminDashboard from './pages/ProgramAdminDashboard';
import OnboardingFlow from './pages/OnboardingFlow';
import GoalsPage from './pages/GoalsPage';
import AssessmentBuilderPage from './pages/AssessmentBuilderPage';
import CustomAssessmentPage from './pages/CustomAssessmentPage';
import PeopleLikeYouPage from './pages/PeopleLikeYouPage';
import SummerProjectPage from './pages/SummerProjectPage';
import ParentDashboard from './pages/ParentDashboard';
import LeadMonitorDashboard from './pages/LeadMonitorDashboard';
import WeeklySurveyPage from './pages/WeeklySurveyPage';
import HelpDeskPage from './pages/HelpDeskPage';
import ResourceCenterPage from './pages/ResourceCenterPage';
import BadgesShowcasePage from './pages/BadgesShowcasePage';
import AccomplishmentsPage from './pages/AccomplishmentsPage';
import YouthDetailPage from './pages/YouthDetailPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import PasswordChangeModal from './components/PasswordChangeModal';
import ThemeConfigPage from './pages/ThemeConfigPage';
import ReportsPage from './pages/ReportsPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import SYEPLaunchPage from './pages/SYEPLaunchPage';
import UserFlowGuide from './pages/UserFlowGuide';
import AIShowcasePage from './pages/AIShowcasePage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import OrgDetailPage from './pages/OrgDetailPage';
import CompletionTracker from './pages/CompletionTracker';
import ParentConsentPage from './pages/ParentConsentPage';
import ContentModerationPage from './pages/ContentModerationPage';
import AgreementAnalyticsPage from './pages/AgreementAnalyticsPage';
import AgreementsFlow from './components/AgreementsFlow';

// GPS (Student Pathways) Dashboards
import GPSStudentDashboard from './pages/GPSStudentDashboard';
import GPSCounselorDashboard from './pages/GPSCounselorDashboard';
import GPSParentDashboard from './pages/GPSParentDashboard';

// Other Program Model Dashboards
import YouthDevDashboard from './pages/YouthDevDashboard';
import YouthLeadershipDashboard from './pages/YouthLeadershipDashboard';
import WorkforceDashboard from './pages/WorkforceDashboard';

// AI Guide — universal for all models
import AIGuidePage from './pages/AIGuidePage';

// Theme Context
import { ThemeProvider } from './contexts/ThemeContext';

// Design Preview Pages
import SummerArcadePreview from './pages/design-previews/SummerArcadePreview';
import CleanSlatePreview from './pages/design-previews/CleanSlatePreview';
import NightModePreview from './pages/design-previews/NightModePreview';
import DaydreamPreview from './pages/design-previews/DaydreamPreview';
import YASDesignV1 from './pages/design-previews/YASDesignV1';
import YASDesignV2 from './pages/design-previews/YASDesignV2';
import YASDesignV3 from './pages/design-previews/YASDesignV3';
import YASDesignV4 from './pages/design-previews/YASDesignV4';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [agreementsComplete, setAgreementsComplete] = useState(false);
  const [checkingAgreements, setCheckingAgreements] = useState(true);

  const checkAgreementStatus = useCallback(async (token) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/agreements/required/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setAgreementsComplete(data.can_access_platform);
      } else {
        // If API fails, allow access (graceful degradation)
        setAgreementsComplete(true);
      }
    } catch (error) {
      console.error('Failed to check agreements:', error);
      setAgreementsComplete(true);
    }
    setCheckingAgreements(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentUser = useCallback(async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        // Check if user needs to change password (from /auth/me or localStorage flag)
        if (data.needs_password_change || localStorage.getItem('needsPasswordChange') === 'true') {
          setNeedsPasswordChange(true);
        }
        // Check agreement status for youth role
        if (data.role === 'youth') {
          await checkAgreementStatus(token);
        } else {
          setAgreementsComplete(true);
          setCheckingAgreements(false);
        }
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('needsPasswordChange');
        setCheckingAgreements(false);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setCheckingAgreements(false);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkAgreementStatus]);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        await fetchCurrentUser(token);
      } else {
        setLoading(false);
        setCheckingAgreements(false);
      }
    };
    initializeAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCurrentUser]);

  const getDefaultRoute = (userRole) => {
    switch (userRole) {
      case 'youth':
        return '/dashboard';
      case 'coach':
        return '/coach';
      case 'admin':
      case 'program_admin':
        return '/admin';
      case 'lead_monitor':
        return '/lead-monitor';
      case 'parent':
        return '/parent';
      case 'platform_admin':
        return '/platform';
      default:
        return '/dashboard';
    }
  };

  if (loading || checkingAgreements) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  // Show agreements flow for youth who haven't completed agreements
  // BUT skip if on a public page (parent-about, parent-how-to, parent-consent)
  const currentPath = window.location.pathname;
  const publicPaths = ['/parent-about', '/parent-how-to', '/parent-consent', '/how-to-use', '/preview/', '/login', '/register', '/forgot-password', '/reset-password', '/tutorial', '/demo', '/programs', '/home', '/how-it-works', '/org/', '/newark-syep', '/guide', '/ai', '/contact', '/about'];
  const isPublicPage = publicPaths.some(p => currentPath.startsWith(p));
  
  if (user && user.role === 'youth' && !agreementsComplete && !needsPasswordChange && !isPublicPage) {
    return (
      <ThemeProvider user={user}>
        <div className="App">
          <AgreementsFlow 
            user={user} 
            onComplete={() => setAgreementsComplete(true)} 
          />
          <Toaster position="top-center" richColors />
        </div>
      </ThemeProvider>
    );
  }

  const exitImpersonation = () => {
    const originalToken = localStorage.getItem('original_admin_token');
    if (originalToken) {
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('original_admin_token');
      localStorage.removeItem('impersonating');
      window.location.href = '/platform-admin';
      window.location.reload();
    }
  };

  return (
    <ThemeProvider user={user}>
      <div className="App">
        {/* Impersonation Banner */}
        {localStorage.getItem('impersonating') === 'true' && (
          <div style={{ background: '#E86C3D', color: 'white', padding: '8px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, position: 'sticky', top: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
            <span>Viewing as: {user?.name} ({user?.role})</span>
            <button onClick={exitImpersonation} style={{ background: 'white', color: '#E86C3D', border: 'none', padding: '4px 12px', borderRadius: '6px', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>
              Exit &amp; Return to Platform Admin
            </button>
          </div>
        )}
        <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={!user ? <Login setUser={setUser} setNeedsPasswordChange={setNeedsPasswordChange} /> : <Navigate to={getDefaultRoute(user.role)} />} 
          />
          <Route 
            path="/register" 
            element={!user ? <Register setUser={setUser} /> : <Navigate to={getDefaultRoute(user.role)} />} 
          />
          <Route 
            path="/forgot-password" 
            element={<ForgotPasswordPage />} 
          />
          <Route 
            path="/reset-password" 
            element={<ForgotPasswordPage />} 
          />
          
          {/* Public Website Pages */}
          <Route path="/parent-consent" element={<ParentConsentPage />} />
          <Route path="/parent-about" element={<ParentAboutPage />} />
          <Route path="/parent-how-to" element={<ParentHowToPage />} />
          <Route path="/tutorial" element={<YouthTutorialPage />} />
          <Route path="/tutorial/staff" element={<StaffTutorialPage />} />
          <Route path="/tutorial/admin" element={<AdminTutorialPage />} />
          <Route path="/tutorial/parent" element={<ParentAboutPage />} />
          <Route path="/demo" element={<DemoRequestPage />} />
          <Route path="/programs" element={<ProgramsPage />} />
          <Route path="/programs/syep" element={<ProgramsPage />} />
          <Route path="/programs/schools" element={<ProgramsPage />} />
          <Route path="/programs/nonprofit" element={<ProgramsPage />} />
          <Route path="/programs/workforce" element={<ProgramsPage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/how-it-works" element={<YouthTutorialPage />} />
          
          {/* How to Use Guide - Public */}
          <Route path="/how-to-use" element={<HowToUsePage />} />
          <Route path="/newark-syep" element={<SYEPLaunchPage />} />
          <Route path="/newark-syep/guide" element={<UserFlowGuide />} />
          <Route path="/guide" element={<UserFlowGuide />} />
          <Route path="/ai" element={<AIShowcasePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          
          {/* Org Branded Login - Public */}
          <Route path="/org/:slug" element={<OrgLoginPage setUser={setUser} />} />
          
          {/* Design Preview Routes - Public for review */}
          <Route path="/preview/summer-arcade" element={<SummerArcadePreview />} />
          <Route path="/preview/clean-slate" element={<CleanSlatePreview />} />
          <Route path="/preview/night-mode" element={<NightModePreview />} />
          <Route path="/preview/daydream" element={<DaydreamPreview />} />
          <Route path="/preview/yas-v1" element={<YASDesignV1 />} />
          <Route path="/preview/yas-v2" element={<YASDesignV2 />} />
          <Route path="/preview/yas-v3" element={<YASDesignV3 />} />
          <Route path="/preview/yas-v4" element={<YASDesignV4 />} />
          
          {/* Youth Routes */}
          {user && user.role === 'youth' && (
            <>
              <Route path="/dashboard" element={
                user.program_type === 'student_pathways_gps' ? <GPSStudentDashboard user={user} setUser={setUser} /> :
                user.program_type === 'youth_development' ? <YouthDevDashboard user={user} setUser={setUser} /> :
                user.program_type === 'youth_leadership' ? <YouthLeadershipDashboard user={user} setUser={setUser} /> :
                user.program_type === 'workforce_development' ? <WorkforceDashboard user={user} setUser={setUser} /> :
                <ThemedYouthDashboard user={user} setUser={setUser} />
              } />
              <Route path="/dashboard-legacy" element={<YouthDashboard user={user} setUser={setUser} />} />
              <Route path="/onboarding" element={<OnboardingFlow user={user} setUser={setUser} onComplete={() => window.location.href = '/dashboard'} />} />
              <Route path="/checkin" element={<CheckInWizard user={user} />} />
              <Route path="/trivia" element={<TriviaQuiz user={user} />} />
              <Route path="/skills-tracker" element={<SkillsAssessment user={user} />} />
              <Route path="/leaderboard" element={<Leaderboard user={user} />} />
              <Route path="/wins" element={<WinsWallPage user={user} />} />
              <Route path="/profile" element={<Profile user={user} setUser={setUser} />} />
              <Route path="/team" element={<TeamPage user={user} />} />
              <Route path="/goals" element={<GoalsPage user={user} />} />
              <Route path="/summer-project" element={<ImpactProjectPage user={user} />} />
              <Route path="/assessment" element={<SJTAssessmentPage user={user} />} />
              <Route path="/custom-assessments" element={<CustomAssessmentPage user={user} />} />
              <Route path="/custom-assessment/:assessmentId" element={<CustomAssessmentPage user={user} />} />
              <Route path="/people-like-you" element={<PeopleLikeYouPage user={user} />} />
              <Route path="/weekly-survey" element={<WeeklySurveyPage user={user} />} />
              <Route path="/helpdesk" element={<HelpDeskPage user={user} />} />
              <Route path="/resources" element={<ResourceCenterPage user={user} />} />
              <Route path="/inbox" element={<InboxPage user={user} />} />
              <Route path="/badges" element={<BadgesShowcasePage user={user} />} />
              <Route path="/accomplishments" element={<AccomplishmentsPage user={user} />} />
              <Route path="/interests" element={<InterestsExplorer user={user} />} />
              <Route path="/skills" element={<SkillsTracker user={user} />} />
              <Route path="/vault" element={<VaultPage user={user} />} />
              <Route path="/ai-counselor" element={<AICareerCounselor user={user} />} />
              <Route path="/ai-guide" element={<AIGuidePage user={user} />} />
              <Route path="/events" element={<EventsPage user={user} />} />
              <Route path="/workshops" element={<CoursesListPage user={user} />} />
              <Route path="/courses" element={<CoursesListPage user={user} />} />
              <Route path="/workbook" element={<WorkbookPage user={user} />} />
              <Route path="/course/:courseId" element={<CoursePage user={user} />} />
              <Route path="/circles" element={<CirclesPage user={user} />} />
            </>
          )}
          
          {/* Coach Routes */}
          {user && user.role === 'coach' && (
            <>
              <Route path="/coach" element={<LeadMonitorDashboard user={user} setUser={setUser} />} />
              <Route path="/admin" element={<AdminDashboard user={user} setUser={setUser} />} />
              <Route path="/help-guides" element={<HelpGuidesPage user={user} />} />
            </>
          )}

          {/* Admin Routes */}
          {user && user.role === 'admin' && (
            <>
              <Route path="/admin" element={<AdminDashboard user={user} setUser={setUser} />} />
              <Route path="/coach" element={<LeadMonitorDashboard user={user} setUser={setUser} />} />
              <Route path="/help-guides" element={<HelpGuidesPage user={user} />} />
              <Route path="/wins" element={<WinsWallPage user={user} />} />
              <Route path="/admin/theme" element={<ThemeConfigPage user={user} />} />
              <Route path="/admin/reports" element={<ReportsPage user={user} />} />
              <Route path="/admin/analytics" element={<AnalyticsDashboard user={user} />} />
              <Route path="/admin/completion-tracker" element={<CompletionTracker user={user} />} />
              <Route path="/admin/moderation" element={<ContentModerationPage user={user} />} />
              <Route path="/admin/agreements" element={<AgreementAnalyticsPage user={user} />} />
            </>
          )}

          {/* Program Admin Routes - Same as Admin */}
          {user && user.role === 'program_admin' && (
            <>
              <Route path="/admin" element={<AdminDashboard user={user} setUser={setUser} />} />
              <Route path="/coach" element={<LeadMonitorDashboard user={user} setUser={setUser} />} />
              <Route path="/help-guides" element={<HelpGuidesPage user={user} />} />
              <Route path="/wins" element={<WinsWallPage user={user} />} />
              <Route path="/admin/theme" element={<ThemeConfigPage user={user} />} />
              <Route path="/admin/reports" element={<ReportsPage user={user} />} />
              <Route path="/admin/analytics" element={<AnalyticsDashboard user={user} />} />
              <Route path="/admin/completion-tracker" element={<CompletionTracker user={user} />} />
              <Route path="/admin/moderation" element={<ContentModerationPage user={user} />} />
              <Route path="/admin/agreements" element={<AgreementAnalyticsPage user={user} />} />
              <Route path="/admin/assessment-builder" element={<AssessmentBuilderPage user={user} />} />
              <Route path="/youth/:youthId" element={<YouthDetailPage user={user} />} />
            </>
          )}

          {/* Platform Admin Routes */}
          {user && user.role === 'platform_admin' && (
            <>
              <Route path="/platform" element={<PlatformAdminDashboard user={user} setUser={setUser} />} />
              <Route path="/platform-admin" element={<PlatformAdminDashboard user={user} setUser={setUser} />} />
              <Route path="/platform/org/:orgId" element={<OrgDetailPage user={user} />} />
              <Route path="/platform/completion-tracker" element={<CompletionTracker user={user} />} />
              <Route path="/admin/program/:programId" element={<ProgramDetailPage user={user} />} />
              <Route path="/admin/dashboard" element={<ProgramAdminDashboard user={user} />} />
              <Route path="/admin" element={<AdminDashboard user={user} setUser={setUser} />} />
              <Route path="/admin/analytics" element={<AnalyticsDashboard user={user} />} />
              <Route path="/admin/completion-tracker" element={<CompletionTracker user={user} />} />
              <Route path="/interests" element={<InterestsExplorer user={user} />} />
              <Route path="/skills" element={<SkillsTracker user={user} />} />
              <Route path="/vault" element={<VaultPage user={user} />} />
              <Route path="/portfolio/:userId" element={<VaultPage user={user} />} />
              <Route path="/circles" element={<CirclesPage user={user} />} />
              <Route path="/admin/theme" element={<ThemeConfigPage user={user} />} />
              <Route path="/admin/reports" element={<ReportsPage user={user} />} />
              <Route path="/admin/moderation" element={<ContentModerationPage user={user} />} />
              <Route path="/admin/agreements" element={<AgreementAnalyticsPage user={user} />} />
              <Route path="/admin/assessment-builder" element={<AssessmentBuilderPage user={user} />} />
              <Route path="/youth/:youthId" element={<YouthDetailPage user={user} />} />
            </>
          )}

          {/* Lead Monitor / School Counselor Routes */}
          {user && user.role === 'lead_monitor' && (
            <>
              <Route path="/lead-monitor" element={
                user.program_type === 'student_pathways_gps'
                  ? <GPSCounselorDashboard user={user} setUser={setUser} />
                  : <LeadMonitorDashboard user={user} setUser={setUser} />
              } />
              <Route path="/helpdesk" element={<HelpDeskPage user={user} />} />
              <Route path="/youth/:youthId" element={<YouthDetailPage user={user} />} />
              <Route path="/events" element={<EventsPage user={user} />} />
              <Route path="/help-guides" element={<HelpGuidesPage user={user} />} />
            </>
          )}

          {/* Parent Routes */}
          {user && user.role === 'parent' && (
            <>
              <Route path="/parent" element={
                user.program_type === 'student_pathways_gps'
                  ? <GPSParentDashboard user={user} setUser={setUser} />
                  : <ParentDashboard user={user} setUser={setUser} />
              } />
              <Route path="/helpdesk" element={<HelpDeskPage user={user} />} />
            </>
          )}

          {/* Announcements History - available to all logged in users */}
          {user && (
            <Route path="/announcements" element={<AnnouncementsPage user={user} />} />
          )}
          
          {/* Default Redirect */}
          <Route 
            path="/" 
            element={user ? <Navigate to={getDefaultRoute(user.role)} /> : <HomePage />} 
          />
          <Route 
            path="*" 
            element={<Navigate to={user ? getDefaultRoute(user.role) : '/'} />} 
          />
        </Routes>
        
        {/* Password Change Modal - shows for users who need to change their temporary password */}
        {user && needsPasswordChange && (
          <PasswordChangeModal 
            isOpen={true} 
            onComplete={() => {
              setNeedsPasswordChange(false);
              localStorage.removeItem('needsPasswordChange');
              // Update user state to reflect password change
              setUser(prev => ({ ...prev, needs_password_change: false }));
            }} 
          />
        )}
      </BrowserRouter>
      <Toaster position="top-center" richColors />
      {user && user.role === 'youth' && <MustReadPopup />}
      {user && user.role === 'youth' && <InstallPrompt user={user} />}
    </div>
    </ThemeProvider>
  );
}

export default App;
